// Mic capture in the page (webview or browser), ported from live-voice-ai with its iOS
// handling intact. Chromium's (and WebView2's) WebRTC audio processing (AEC3, noise
// suppression, AGC) runs on this track; echo cancellation needs the kit's audio to be played
// by this same page (see player.ts).
//
// MicCapture is also the capture manager other hosts reuse (README: "Mic capture on phones"):
// one shared AudioContext with the player (./audio-hub.ts), a watchdog that recovers the mic by
// itself where iOS allows it (./mic-recovery.ts), recover() for the host, and events for the
// host's log and UI (on()).

import { AudioHub } from "./audio-hub";
import { MicRecovery, WATCH_MS, type MicDecision, type RebuildReason } from "./mic-recovery";
import { MIC_SAMPLE_RATE } from "./protocol";

export const MIC_CONSTRAINTS: MediaTrackConstraints = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  channelCount: 1,
};

export interface Capture {
  /** Starts the mic; resolves once audio is really arriving. Frames are PCM16 16 kHz. */
  start(onFrame: (pcm: Int16Array) => void): Promise<void>;
  stop(): void;
  level(): number;
  /**
   * The mic is really capturing: started, audio arrived in the last `withinMs`, and its track
   * isn't muted (iOS feeds a muted track silence). Pages show "Mic on" from this alone. Captures without it count as live while started.
   */
  live?(withinMs?: number): boolean;
  /** iOS: call synchronously inside the tap that starts a session. */
  prepare?(): void;
  stats?(): Record<string, unknown>;
}

/** The mic gave no audio even after a quiet retry: usually iOS still holds it (a phone call). */
export class MicSilentError extends Error {
  constructor() {
    super("The mic isn't picking anything up. Try again in a moment.");
    this.name = "MicSilentError";
  }
}

// Runs on the audio thread: batches 128-sample render quanta into ~21 ms blocks. Loaded from a
// Blob URL, so hosts don't have to serve a separate worklet file; pages whose CSP forbids blob:
// scripts (the phone page) serve it as a file and pass `workletUrl`. Its one output (silence)
// goes through a zero gain to the destination: Safari only reliably pulls nodes that reach it.
export const WORKLET_SOURCE = `
class VoiceKitCapture extends AudioWorkletProcessor {
  constructor() { super(); this.block = new Float32Array(1024); this.n = 0; }
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (ch) {
      for (let i = 0; i < ch.length; i++) {
        this.block[this.n++] = ch[i];
        if (this.n === this.block.length) { this.port.postMessage(this.block.slice(0)); this.n = 0; }
      }
    }
    return true;
  }
}
registerProcessor("voice-kit-capture", VoiceKitCapture);
`;

let workletUrl: string | null = null;
function workletModuleUrl(): string {
  workletUrl ??= URL.createObjectURL(new Blob([WORKLET_SOURCE], { type: "application/javascript" }));
  return workletUrl;
}

/**
 * A new mic track would need a permission prompt and there is no gesture to ask in (a
 * home-screen web app, or the site set to "Ask"): the page asks for one tap instead.
 */
export class MicNeedsGesture extends Error {
  constructor() {
    super("The mic needs a tap to come back");
    this.name = "MicNeedsGesture";
  }
}

/** A start was cancelled by stop() or a newer start (a second tap): not a mic failure. */
export class MicStartCancelled extends Error {
  constructor() {
    super("Mic start cancelled");
    this.name = "MicStartCancelled";
  }
}

/**
 * Per attempt: how long the context may take to reach "running" and deliver its first frame.
 * After the page was in the background, iOS often takes 3-4.5 s to run the audio context
 * after the tap (phone diagnostics: ctxRunningMs 3000-4500), so a shorter wait gave up on a
 * mic that was on its way, and a rebuild stacked on top of the slow context made it worse.
 */
export const WAKE_MS = 6000;
/**
 * An automatic rebuild mid-conversation (the page is visible and its audio was running) waits
 * less: the phone's logs show a fresh context running in 1-2 s there, and the user is in silence.
 */
export const REBUILD_WAKE_MS = 4000;
/** Once the context runs, the first frame gets at least this long (even past WAKE_MS). */
export const FIRST_FRAME_MS = 1500;
/** While the context isn't running yet, resume() is asked again this often (it can hang). */
const RESUME_EVERY_MS = 1000;
const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/** The mic's input as the track reports it: which device, at what rate. */
export interface MicRoute {
  label: string;
  deviceId: string | null;
  /** The track's sample rate (null: the browser doesn't say). */
  trackRate: number | null;
  /** The audio context's rate (what the resampler takes in). */
  ctxRate: number;
  channels: number | null;
}

/** What MicCapture tells its host (`on()`); the phone page logs each one to `client.log`. */
export type MicEvent =
  | { type: "first_frame"; ttffMs: number; route: MicRoute | null; streamReused: boolean }
  | { type: "rebuild"; reason: RebuildReason; attempt: number; route: MicRoute | null }
  | { type: "rebuilt"; reason: RebuildReason; attempt: number; ok: boolean; ttffMs: number | null; route: MicRoute | null; error?: string }
  | { type: "route"; route: MicRoute; previous: MicRoute | null }
  | { type: "mute" }
  | { type: "unmute" }
  /** Out of automatic attempts: the mic needs a tap. The next prepare() starts afresh. */
  | { type: "gesture_needed"; reason: RebuildReason }
  /** getUserMedia was asked while the permission said "prompt": the user likely saw a prompt. */
  | { type: "permission_prompt"; gumMs: number };

export type MicEventType = MicEvent["type"];

export interface MicCaptureOptions {
  /** The input device to ask for (desktop pickers). */
  deviceId?: () => string | undefined;
  /** The worklet as a file (pages whose CSP forbids blob: scripts). */
  workletUrl?: string;
  /** The AudioContext to share with the player (one per page). Default: a hub of its own. */
  hub?: AudioHub;
  /**
   * Keep the mic's stream this long after stop(), its frames dropped, so a restart reuses it:
   * no new permission prompt (iOS home-screen web apps ask again for every getUserMedia) and no
   * route flip. 0 (default): release it at once. For stops inside a conversation (reopening
   * the mic, switching a typed session to voice); when the user ends voice, the host calls
   * release() and the mic goes at once. A hidden page always releases it.
   */
  keepStreamMs?: number;
  /**
   * The page runs as a home-screen web app (standaloneMode()). There getUserMedia may prompt
   * even after "Allow", so without a gesture the capture never calls it: it clones the live
   * track, or asks for the tap. Default: detected.
   */
  standalone?: boolean;
  /** Fix the mic by itself where iOS allows it (./mic-recovery.ts). Default true. */
  autoRecover?: boolean;
  /**
   * After getUserMedia, rebuild the context once if it runs at another rate than the track (iOS:
   * the mic opening flips the route to Bluetooth HFP at 16 or 24 kHz under a context made at 48).
   * Default: on iOS / iPadOS only (desktop browsers resample a mismatch fine).
   */
  rebindOnRateMismatch?: boolean;
}

/**
 * Mic capture on ONE AudioContext, shared with the player through an AudioHub. iOS Safari
 * hands a page a silent pipeline in several ways (a context made after an await once
 * backgrounded, a route flip to Bluetooth HFP, a call), so: the context is created or resumed
 * inside the user's tap (prepare()), the mic is asked for in that same tap, and the stream is
 * (re)bound to the context only after getUserMedia resolved. While it runs, a watchdog
 * (MicRecovery) rebuilds it, a fresh context AND a fresh track, whenever a live track goes
 * silent, its rate or route changes, the context freezes, or a muted track doesn't come back;
 * with backoff, and once out of attempts it asks for one tap ("gesture_needed").
 */
export class MicCapture implements Capture {
  readonly hub: AudioHub;
  private readonly opts: MicCaptureOptions;
  private readonly deviceId: () => string | undefined;
  private stream: MediaStream | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private node: AudioWorkletNode | null = null;
  private sink: GainNode | null = null;
  /** The context the source and node are on. */
  private boundCtx: AudioContext | null = null;
  private boundAt = 0;
  private boundTrackRate: number | null = null;
  private route: MicRoute | null = null;
  private routeChangedAt = 0;
  private readonly worklets = new WeakMap<AudioContext, Promise<void>>();
  private onFrame: ((pcm: Int16Array) => void) | null = null;
  private ds: Downsampler | null = null;
  private rms = 0;
  private blocks = 0;
  private lastBlockAt = 0;
  private startedAt = 0;
  private resumes = 0;
  private retries = 0;
  private lastInterruptedAt = 0;
  /** iOS took the audio away (a call) or muted the track: the next tap builds a fresh context. */
  private wasInterrupted = false;
  /** The mic asked for inside the tap (prepare()), taken by the next start. */
  private pendingMic: Promise<MediaStream> | null = null;
  private timings: Record<string, unknown> = {};
  private starting = false;
  private gen = 0; // bumped by stop(): a start still in flight notices and backs out
  private readonly policy = new MicRecovery();
  private watch: ReturnType<typeof setInterval> | null = null;
  private releaseTimer: ReturnType<typeof setTimeout> | null = null;
  private streamsReused = 0;
  private clones = 0;
  private autoRebuilds = 0;
  private readonly rebuildLog: Array<{ at: number; reason: RebuildReason; attempt: number; ok: boolean | null; ttffMs: number | null; ctxRate: number; trackRate: number | null }> = [];
  private readonly listeners = new Map<MicEventType | "*", Set<(e: MicEvent) => void>>();
  private permission: PermissionState | "unknown" = "unknown";
  /** Track rates a fresh context didn't match either: not rebuilt for again. */
  private readonly mismatchKept = new Set<number>();
  private prompts = 0;
  /** The last start gave up because the mic stayed silent (see MicSilentError). */
  failed = false;
  /** The mic's track was muted / unmuted by the OS (iOS: another app or a brief interruption). */
  onTrackMute: (() => void) | null = null;
  onTrackUnmute: (() => void) | null = null;
  /** The last few mute/unmute events of the mic's tracks, for the phone's diagnostics. */
  private trackEvents: TrackEvent[] = [];
  private tracks = 0;

  constructor(opts?: MicCaptureOptions);
  constructor(deviceId?: () => string | undefined, opts?: MicCaptureOptions);
  constructor(a?: MicCaptureOptions | (() => string | undefined), b: MicCaptureOptions = {}) {
    this.opts = typeof a === "function" ? { ...b, deviceId: a } : { ...b, ...(a ?? {}) };
    this.deviceId = this.opts.deviceId ?? (() => undefined);
    this.hub = this.opts.hub ?? new AudioHub();
    // A context replaced from elsewhere (the player's tap, a route change, mid-rebuild): move
    // the mic onto it. bind() is safe alongside a start's own; the watchdog's "unbound" rule
    // catches a bind that fails.
    this.hub.onSwap((next) => {
      if (!this.boundCtx || this.boundCtx === next) return;
      this.unbind();
      if (this.stream) void this.bind(this.gen).catch(() => {});
    });
    this.hub.onState((ctx) => {
      if ((ctx.state as string) === "interrupted") this.wasInterrupted = true; // iOS: a call took the audio
      if (ctx.state !== "running" && ctx.state !== "closed" && this.onFrame) {
        this.resumes++;
        void ctx.resume().catch(() => {});
      }
    });
    if (typeof document !== "undefined") {
      // Backgrounded (a call, another app): the next tap builds a fresh context.
      document.addEventListener("visibilitychange", () => document.visibilityState === "hidden" && this.markStale());
    }
    const md = typeof navigator !== "undefined" ? navigator.mediaDevices : undefined;
    md?.addEventListener?.("devicechange", () => this.deviceChanged());
    this.watchPermission();
  }

  /** Listen to one kind of event, or to all ("*"). Returns an unsubscribe. */
  on(type: "*", fn: (e: MicEvent) => void): () => void;
  on<T extends MicEventType>(type: T, fn: (e: Extract<MicEvent, { type: T }>) => void): () => void;
  on(type: MicEventType | "*", fn: (e: never) => void): () => void {
    let set = this.listeners.get(type);
    if (!set) this.listeners.set(type, (set = new Set()));
    const f = fn as (e: MicEvent) => void;
    set.add(f);
    return () => set!.delete(f);
  }

  private emit(e: MicEvent): void {
    for (const key of [e.type, "*"] as const) this.listeners.get(key)?.forEach((fn) => fn(e));
  }

  /**
   * Call synchronously from the tap/click that starts a conversation (iOS needs the gesture):
   * asks for the mic (unless a kept stream is reused) and creates or resumes the audio context,
   * both inside the tap.
   */
  prepare(): void {
    if (!this.onFrame && !this.pendingMic && !this.reusable() && typeof navigator !== "undefined" && navigator.mediaDevices) {
      const t0 = performance.now();
      this.timings = {};
      const mic = this.getMic();
      mic.then(
        () => (this.timings.gumMs = Math.round(performance.now() - t0)),
        () => {},
      );
      this.pendingMic = mic;
    }
    // After a call or a muted track: afresh (unless this tap already built one, the player's).
    if (!this.onFrame && this.wasInterrupted && this.hub.msSinceBuild() > SAME_TAP_MS) this.hub.freshOnNextTap("after_interruption");
    const before = this.hub.ctx;
    this.hub.prepare();
    if (this.hub.ctx !== before) this.wasInterrupted = false;
  }

  /** The page went to the background: a kept stream goes, and the next tap builds afresh. */
  markStale(): void {
    this.hub.freshOnNextTap("after_background");
    if (!this.onFrame) this.releaseMic();
  }

  /**
   * Starts the mic and resolves once audio is actually arriving. Each attempt waits up to
   * WAKE_MS for the context to run and the first frame to arrive (a slow context after the
   * background is waited for, never rebuilt mid-wake). If nothing comes, it starts over once,
   * quietly, with a fresh audio context AND a fresh track (a live track on a running context
   * can still be deaf: reusing the context kept it deaf). If it is still silent, throws
   * MicSilentError (the page's last resort is a reload).
   */
  async start(onFrame: (pcm: Int16Array) => void): Promise<void> {
    this.prepare();
    const gen = ++this.gen;
    this.onFrame = onFrame;
    this.starting = true;
    this.policy.reset();
    this.stopWatch();
    const t0 = performance.now();
    try {
      await this.acquire(gen, t0);
      if (await this.wake(gen, t0)) return this.ok(gen, t0);
      this.retries++;
      const track = this.track();
      Object.assign(this.timings, { firstCtx: this.hub.ctx?.state ?? "none", firstTrackMuted: track?.muted ?? null });
      try {
        await this.replace(gen, "silent_live");
        if (await this.wake(gen, t0)) return this.ok(gen, t0);
      } catch (e) {
        if (!(e instanceof MicNeedsGesture)) throw e; // no quiet way to a new track: silent
      }
    } finally {
      if (gen === this.gen) this.starting = false;
    }
    const track = this.track();
    Object.assign(this.timings, { finalCtx: this.hub.ctx?.state ?? "none", finalTrackMuted: track?.muted ?? null, finalTrackState: track?.readyState ?? "none" });
    this.stop();
    this.hub.freshOnNextTap("silent");
    this.failed = true;
    throw new MicSilentError();
  }

  private ok(gen: number, t0: number): void {
    this.failed = false;
    this.wasInterrupted = false;
    this.timings.heardMs = Math.round(performance.now() - t0);
    this.emit({ type: "first_frame", ttffMs: Math.round(this.lastBlockAt - t0), route: this.route, streamReused: this.timings.streamReused === true });
    if (gen === this.gen) this.startWatch();
  }

  /**
   * The stream for this start: a kept one if it is still good, else the one asked for in the tap,
   * else a new one. Then bound to the hub's context, after getUserMedia resolved (the context
   * made in the tap may run at the rate the route had before the mic opened: rebuilt once).
   */
  private async acquire(gen: number, t0: number): Promise<void> {
    const pending = this.pendingMic;
    this.pendingMic = null;
    const reuse = !pending && this.reusable();
    this.cancelRelease();
    if (reuse) {
      this.streamsReused++;
      this.timings.streamReused = true;
      // Its frames were flowing while kept: wait for a fresh one all the same.
      this.lastBlockAt = 0;
      this.startedAt = this.boundAt = performance.now();
    } else {
      let stream: MediaStream;
      try {
        stream = await (pending ?? this.getMic());
      } catch (e) {
        if (!pending || gen !== this.gen || !this.canAskQuietly()) throw e;
        stream = await this.getMic(); // the tap's request failed: ask once more now (it can't prompt)
      }
      if (stream.getAudioTracks()[0]?.readyState === "ended") {
        stopTracks(stream);
        if (!this.canAskQuietly()) throw new MicNeedsGesture();
        stream = await this.getMic(); // handed a dead track: ask once more (it can't prompt)
      }
      if (gen !== this.gen) {
        if (stream !== this.stream) stopTracks(stream);
        throw new MicStartCancelled();
      }
      if (stream !== this.stream) {
        this.releaseMic();
        this.adopt(stream);
      }
    }
    this.timings.gumDoneMs = Math.round(performance.now() - t0);
    const ctx = this.hub.context();
    const trackRate = rateOf(this.track());
    if (trackRate && trackRate !== ctx.sampleRate) {
      this.timings.rateMismatch = { ctx: ctx.sampleRate, track: trackRate };
      // Once per track rate: if a fresh context still runs at another rate, that is how this
      // device is (the resampler copes), not a stranded context.
      if ((this.opts.rebindOnRateMismatch ?? isAppleTouch()) && !this.mismatchKept.has(trackRate)) {
        const fresh = this.hub.rebuild("rate_mismatch");
        if (fresh.sampleRate !== trackRate) this.mismatchKept.add(trackRate);
      }
    }
    // resume() can hang while iOS still holds the audio: nudge it, but don't wait on it here.
    // wake() waits for the context to run (WAKE_MS) and keeps nudging it.
    const now = this.hub.context();
    if (now.state !== "running") void now.resume().catch(() => {});
    await this.bind(gen);
  }

  /**
   * Starts over: a new track (got while the old one is still live, so the page keeps its
   * capture and iOS lets the new context run without a tap), then a new context, then bound.
   * Never outside a gesture in a way that could prompt (quietStream).
   */
  private async replace(gen: number, reason: RebuildReason): Promise<void> {
    const stream = await this.quietStream();
    if (gen !== this.gen) {
      stopTracks(stream);
      throw new MicStartCancelled();
    }
    this.releaseMic();
    this.adopt(stream);
    this.hub.rebuild(reason);
    await this.bind(gen);
  }

  /**
   * A new stream without a gesture: getUserMedia only where it can't prompt (the permission is
   * "granted" and not a home-screen web app); else a clone of the live, unmuted track; else
   * MicNeedsGesture (the page asks for one tap, and the tap re-acquires).
   */
  private async quietStream(): Promise<MediaStream> {
    if (this.canAskQuietly()) return this.getMic();
    const track = this.track();
    if (track && track.readyState === "live" && !track.muted && typeof track.clone === "function") {
      this.clones++;
      return new MediaStream([track.clone()]);
    }
    throw new MicNeedsGesture();
  }

  /**
   * The mic can be started without a gesture and without any risk of a prompt (the permission
   * says "granted", and not a home-screen web app): the phone page's return from a short trip
   * to the background.
   */
  canStartQuietly(): boolean {
    return this.canAskQuietly();
  }

  /** getUserMedia can't show a prompt: the permission says "granted", and not a home-screen web app. */
  private canAskQuietly(): boolean {
    return this.permission === "granted" && !(this.opts.standalone ?? standaloneMode());
  }

  /** Puts the stream on the hub's current context (the worklet loaded there first). */
  private async bind(gen: number): Promise<void> {
    for (;;) {
      const stream = this.stream;
      if (!stream) return;
      const ctx = this.hub.context();
      if (this.boundCtx === ctx && this.source) return;
      this.unbind();
      let loaded = this.worklets.get(ctx);
      if (!loaded) this.worklets.set(ctx, (loaded = ctx.audioWorklet.addModule(this.opts.workletUrl ?? workletModuleUrl())));
      await loaded;
      if (gen !== this.gen) throw new MicStartCancelled();
      if (ctx !== this.hub.ctx || stream !== this.stream) continue; // replaced meanwhile
      if (this.boundCtx === ctx && this.source) return; // another bind got there first
      const node = new AudioWorkletNode(ctx, "voice-kit-capture", { numberOfInputs: 1, numberOfOutputs: 1, outputChannelCount: [1] });
      node.port.onmessage = (e: MessageEvent<Float32Array>) => this.onBlock(e.data);
      // Through a zero gain to the destination: Safari keeps pulling a node that reaches it.
      const sink = ctx.createGain();
      sink.gain.value = 0;
      node.connect(sink);
      sink.connect(ctx.destination);
      const source = ctx.createMediaStreamSource(stream);
      source.connect(node);
      this.node = node;
      this.sink = sink;
      this.source = source;
      this.boundCtx = ctx;
      this.ds = new Downsampler(ctx.sampleRate, MIC_SAMPLE_RATE);
      this.startedAt = this.boundAt = performance.now();
      this.lastBlockAt = 0;
      this.boundTrackRate = rateOf(this.track());
      this.routeChangedAt = 0;
      this.noteRoute();
      return;
    }
  }

  private unbind(): void {
    this.source?.disconnect();
    if (this.node) {
      this.node.port.onmessage = null;
      this.node.disconnect();
    }
    this.sink?.disconnect();
    this.source = this.node = this.sink = null;
    this.boundCtx = null;
  }

  /** The route as the track says now; a "route" event when it differs from the last one. */
  private noteRoute(): void {
    const track = this.track();
    const ctx = this.hub.ctx;
    if (!track || !ctx) return;
    const s = settingsOf(track);
    const route: MicRoute = { label: track.label ?? "", deviceId: s.deviceId ?? null, trackRate: s.sampleRate ?? null, ctxRate: ctx.sampleRate, channels: s.channelCount ?? null };
    const prev = this.route;
    this.route = route;
    if (!prev || prev.label !== route.label || prev.deviceId !== route.deviceId || prev.trackRate !== route.trackRate || prev.ctxRate !== route.ctxRate) this.emit({ type: "route", route, previous: prev });
  }

  /** The input devices changed (AirPods in or out): look at the track again. */
  private deviceChanged(): void {
    const track = this.track();
    if (!track || !this.route) return;
    const s = settingsOf(track);
    const moved = (s.deviceId ?? null) !== this.route.deviceId || (track.label ?? "") !== this.route.label;
    if (moved) this.routeChangedAt = performance.now();
    this.noteRoute();
  }

  // -- the watchdog -------------------------------------------------------------------------

  private startWatch(): void {
    if (this.opts.autoRecover === false || this.watch) return;
    this.watch = setInterval(() => this.check(), WATCH_MS);
  }

  private stopWatch(): void {
    if (this.watch) clearInterval(this.watch);
    this.watch = null;
  }

  /** One look: what MicRecovery decides, done. */
  private check(): void {
    if (!this.onFrame) return this.stopWatch();
    this.act(this.policy.decide(this.observe()));
  }

  private observe() {
    const ctx = this.hub.ctx;
    const track = this.track();
    return {
      now: performance.now(),
      visible: typeof document === "undefined" || document.visibilityState !== "hidden",
      starting: this.starting,
      ctxState: (ctx?.state as string | undefined) ?? "none",
      ctxFrozen: this.hub.frozen(),
      trackState: track?.readyState ?? "none",
      trackMuted: track?.muted === true,
      lastFrameAt: this.lastBlockAt,
      bound: !!this.source,
      boundAt: this.boundAt,
      boundTrackRate: this.boundTrackRate,
      trackRate: rateOf(track),
      routeChanged: this.routeChangedAt > 0,
    };
  }

  private act(d: MicDecision): void {
    if (d.kind === "resume") {
      this.resumes++;
      void this.hub.ctx?.resume().catch(() => {});
    } else if (d.kind === "rebuild") {
      this.autoRebuilds++;
      void this.rebuild(d.reason, d.attempt);
    } else if (d.kind === "gesture") {
      this.hub.freshOnNextTap(d.reason); // pre-warm: the tap starts afresh, first time
      this.wasInterrupted = true;
      this.emit({ type: "gesture_needed", reason: d.reason });
    }
  }

  /** A full rebuild in place (new track, new context); resolves whether audio came back. */
  private async rebuild(reason: RebuildReason, attempt: number): Promise<boolean> {
    if (!this.onFrame || this.starting) return false;
    const gen = this.gen;
    this.starting = true;
    const t0 = performance.now();
    const entry = { at: Date.now(), reason, attempt, ok: null as boolean | null, ttffMs: null as number | null, ctxRate: 0, trackRate: null as number | null };
    this.rebuildLog.push(entry);
    if (this.rebuildLog.length > REBUILDS_KEPT) this.rebuildLog.shift();
    this.emit({ type: "rebuild", reason, attempt, route: this.route });
    let ok = false;
    let error: string | undefined;
    let needsTap = false;
    try {
      await this.replace(gen, reason);
      // The context was swapped during the wait (a tap mid-rebuild): back onto the new one.
      if (!this.source && this.stream && gen === this.gen) await this.bind(gen);
      ok = await this.wake(gen, t0, REBUILD_WAKE_MS);
      if (!ok && !this.source && this.stream && gen === this.gen) {
        await this.bind(gen);
        ok = await this.heard(gen);
      }
      if (ok) this.wasInterrupted = false;
    } catch (e) {
      error = (e as Error)?.message ?? String(e);
      needsTap = e instanceof MicNeedsGesture;
    } finally {
      if (gen === this.gen) this.starting = false;
    }
    entry.ok = ok;
    entry.ttffMs = ok ? Math.round(this.lastBlockAt - t0) : null;
    entry.ctxRate = this.hub.ctx?.sampleRate ?? 0;
    entry.trackRate = rateOf(this.track());
    if (gen === this.gen) this.emit({ type: "rebuilt", reason, attempt, ok, ttffMs: entry.ttffMs, route: this.route, ...(error ? { error } : {}) });
    if (needsTap && gen === this.gen) this.act(this.policy.needGesture(reason));
    return ok;
  }

  /**
   * Mid-conversation, by the host: start the mic over in place, a fresh track and context
   * (counted against the same attempts as the watchdog's). Resolves whether audio came back.
   */
  async recover(): Promise<boolean> {
    if (!this.onFrame || this.starting) return false;
    this.retries++;
    const d = this.policy.manual(performance.now());
    if (d.kind !== "rebuild") {
      this.act(d);
      return false;
    }
    return this.rebuild(d.reason, d.attempt);
  }

  /** Records the track's mute/unmute events (with wall-clock times) and passes them on. */
  private adopt(stream: MediaStream): void {
    this.stream = stream;
    const track = stream.getAudioTracks()[0];
    if (!track) return;
    const n = ++this.tracks;
    const got = performance.now();
    const note = (ev: TrackEvent["ev"]) => {
      this.trackEvents.push({ track: n, ev, at: Date.now(), sinceTrackMs: Math.round(performance.now() - got) });
      if (this.trackEvents.length > TRACK_EVENTS_KEPT) this.trackEvents.shift();
    };
    if (track.muted) note("muted_at_start");
    track.onmute = () => {
      note("mute");
      if (this.stream !== stream) return;
      this.emit({ type: "mute" });
      this.onTrackMute?.();
    };
    track.onunmute = () => {
      note("unmute");
      if (this.stream !== stream) return;
      this.emit({ type: "unmute" });
      this.onTrackUnmute?.();
    };
  }

  private getMic(): Promise<MediaStream> {
    const deviceId = this.deviceId();
    const asked = this.permission === "prompt";
    const t0 = performance.now();
    const p = navigator.mediaDevices.getUserMedia({
      audio: { ...MIC_CONSTRAINTS, ...(deviceId ? { deviceId: { exact: deviceId } } : {}) },
    });
    if (asked)
      p.then(
        () => {
          this.prompts++;
          this.emit({ type: "permission_prompt", gumMs: Math.round(performance.now() - t0) });
        },
        () => {},
      );
    return p;
  }

  /** Keeps `permission` current, so getMic() knows (synchronously, in the tap) if it prompts. */
  private watchPermission(): void {
    const perms = typeof navigator !== "undefined" ? (navigator as Navigator & { permissions?: Permissions }).permissions : undefined;
    if (!perms?.query) return;
    perms.query({ name: "microphone" as PermissionName }).then(
      (status) => {
        this.permission = status.state;
        status.onchange = () => (this.permission = status.state);
      },
      () => {},
    );
  }

  /** A kept stream that can carry on: live, unmuted, the device asked for. */
  private reusable(): boolean {
    const track = this.track();
    if (!track || track.readyState !== "live" || track.muted || !this.releaseTimer) return false;
    if (performance.now() - this.lastBlockAt > KEEP_IF_FRAME_WITHIN_MS) return false; // went quiet while kept
    const want = this.deviceId();
    return !want || settingsOf(track).deviceId === want;
  }

  private track(): MediaStreamTrack | undefined {
    return this.stream?.getAudioTracks()[0];
  }

  /**
   * Waits for the context to run and the first frame to arrive: up to WAKE_MS from now, and
   * at least FIRST_FRAME_MS after the context started running. resume() is asked again every
   * RESUME_EVERY_MS while it isn't running. Records when the context ran and the first frame.
   */
  private async wake(gen: number, t0: number, budgetMs = WAKE_MS): Promise<boolean> {
    const begun = performance.now();
    let runningAt = 0;
    let nudged = begun;
    for (;;) {
      const ctx = this.hub.ctx;
      if (this.lastBlockAt) {
        this.timings.firstFrameMs = Math.round(this.lastBlockAt - t0);
        return true;
      }
      const now = performance.now();
      if (!runningAt && ctx?.state === "running") {
        runningAt = now;
        this.timings.ctxRunningMs = Math.round(now - t0);
      }
      const until = runningAt ? Math.max(begun + budgetMs, runningAt + FIRST_FRAME_MS) : begun + budgetMs;
      if (now >= until || !ctx || ctx.state === "closed") break;
      if (!runningAt && now - nudged >= RESUME_EVERY_MS) {
        nudged = now;
        this.resumes++;
        void ctx.resume().catch(() => {});
      }
      await sleep(50);
      if (gen !== this.gen) throw new MicStartCancelled();
    }
    if (!runningAt) this.timings.ctxRunningMs = null;
    return this.lastBlockAt > 0;
  }

  private async heard(gen: number, ms = 1500): Promise<boolean> {
    for (let waited = 0; waited < ms; waited += 50) {
      if (this.lastBlockAt) return true;
      await sleep(50);
      if (gen !== this.gen) throw new MicStartCancelled();
    }
    return this.lastBlockAt > 0;
  }

  private onBlock(block: Float32Array): void {
    this.blocks++;
    this.lastBlockAt = performance.now();
    if (!this.onFrame || !this.ds) return; // a kept stream: frames dropped
    let s = 0;
    for (let i = 0; i < block.length; i++) s += block[i] * block[i];
    this.rms = Math.sqrt(s / block.length);
    this.onFrame(this.ds.push(block));
  }

  private releaseMic(): void {
    this.cancelRelease();
    this.unbind();
    this.stream?.getTracks().forEach((t) => {
      t.onmute = t.onunmute = null;
      t.stop();
    });
    this.stream = null;
    this.route = null;
  }

  private cancelRelease(): void {
    if (this.releaseTimer) clearTimeout(this.releaseTimer);
    this.releaseTimer = null;
  }

  stop(): void {
    // Only a mic that was really delivering is worth keeping (never a stalled or given-up one).
    const healthy = this.live(KEEP_IF_FRAME_WITHIN_MS) && !this.policy.gaveUp && !this.failed;
    // Stopped while it had gone quiet (a tap to reconnect a stalled mic): the next tap starts afresh.
    if (this.onFrame && this.source && !healthy) this.hub.freshOnNextTap("stalled");
    this.gen++;
    this.starting = false;
    this.onFrame = null;
    this.stopWatch();
    // A muted track (iOS held the mic): the next start builds a fresh context too, not just a track.
    if (this.trackMuted()) {
      this.wasInterrupted = true;
      this.hub.freshOnNextTap("track_muted");
    }
    const track = this.track();
    const keep = this.opts.keepStreamMs ?? 0;
    const visible = typeof document === "undefined" || document.visibilityState !== "hidden";
    if (keep > 0 && visible && healthy && track?.readyState === "live") {
      // Kept for a quick restart (no new prompt, no route flip); its frames are dropped.
      this.cancelRelease();
      this.releaseTimer = setTimeout(() => {
        this.releaseTimer = null;
        if (!this.onFrame) this.releaseMic();
      }, keep);
    } else this.releaseMic();
    // A mic asked for in a tap whose start never came: let it go.
    this.pendingMic?.then(stopTracks, () => {});
    this.pendingMic = null;
    this.rms = 0;
    this.startedAt = 0;
    this.lastInterruptedAt = 0;
  }

  /**
   * Lets the mic go now, a kept stream included. Call it when the user ends voice ("Voice
   * ended" means the mic is off); stop() alone keeps the stream for keepStreamMs.
   */
  release(): void {
    if (this.onFrame) this.stop();
    this.releaseMic();
  }

  /**
   * True while the OS has taken the audio away (iOS: a call): the context is "interrupted" or
   * the mic's track is muted. (A track that ended is a stall: stalled() and recover().)
   */
  interrupted(): boolean {
    const held = this.contextInterrupted() || this.trackMuted();
    if (held) this.wasInterrupted = true;
    return held;
  }

  /** The mic's audio context is "interrupted": iOS took the audio itself away (a call). */
  contextInterrupted(): boolean {
    return this.hub.interrupted();
  }

  /**
   * The mic's track is muted: the OS gives this page silence (iOS: another app holds the mic,
   * or a brief interruption left it muted). Not a call by itself: the speaker can be fine. The
   * track's unmute event (onTrackUnmute) says when it is back; the watchdog fetches a fresh one
   * after MUTE_GRACE_MS; a tap can too.
   */
  trackMuted(): boolean {
    return this.track()?.muted === true;
  }

  /**
   * The interruption ended (a call was dismissed): get the mic back in place, without a tap if
   * iOS allows it. The context is resumed (up to 1 s) and a fresh track bound to it; false when
   * that needs a tap, whose start() then builds a fresh context and track. A track that still
   * gives nothing is rebuilt by the watchdog.
   */
  async resumeAfterInterruption(): Promise<boolean> {
    const ctx = this.hub.ctx;
    if (!this.onFrame || !ctx || this.starting) return false;
    if (ctx.state !== "running") await Promise.race([ctx.resume().catch(() => {}), sleep(1000)]);
    if (ctx !== this.hub.ctx || ctx.state !== "running") return false;
    const gen = this.gen;
    this.starting = true;
    this.retries++;
    try {
      const stream = await this.quietStream();
      if (gen !== this.gen) {
        stopTracks(stream);
        return false;
      }
      this.releaseMic();
      this.adopt(stream);
      await this.bind(gen);
      const ok = await this.heard(gen);
      if (ok) {
        this.wasInterrupted = false;
        this.hub.clearInterrupted();
        this.policy.reset();
      }
      return ok;
    } catch {
      return false;
    } finally {
      if (gen === this.gen) this.starting = false;
    }
  }

  /** True when the mic has delivered nothing for `ms` (iOS sometimes goes silent). */
  stalled(ms = 2500): boolean {
    if (!this.onFrame || !this.source || this.starting) return false;
    const now = performance.now();
    if (this.interrupted()) {
      this.lastInterruptedAt = now;
      return false;
    }
    const since = Math.max(this.lastBlockAt || this.startedAt, this.lastInterruptedAt);
    return now - since > ms;
  }

  /** The watchdog is out of attempts: the mic needs a tap (cleared by the next start). */
  get needsGesture(): boolean {
    return this.policy.gaveUp;
  }

  stats(): Record<string, unknown> {
    const track = this.track();
    const ctx = this.hub.ctx;
    return {
      blocks: this.blocks,
      msSinceBlock: this.lastBlockAt ? Math.round(performance.now() - this.lastBlockAt) : null,
      ctx: ctx?.state ?? "none",
      rate: ctx?.sampleRate ?? 0,
      ctxTime: ctx ? Math.round(ctx.currentTime * 10) / 10 : 0,
      route: this.route ? { ...this.route } : null,
      trackMuted: track?.muted ?? null,
      interrupted: this.contextInterrupted() || this.trackMuted(), // read-only: not interrupted()
      trackState: track?.readyState ?? "none",
      resumes: this.resumes,
      retries: this.retries,
      rebuilds: this.hub.builds,
      autoRebuilds: this.autoRebuilds,
      attempts: this.policy.attempts,
      needsGesture: this.policy.gaveUp,
      wasInterrupted: this.wasInterrupted,
      streamsReused: this.streamsReused,
      clones: this.clones,
      quietGum: this.canAskQuietly(),
      streamKept: !!this.releaseTimer,
      permission: this.permission,
      prompts: this.prompts,
      rebuildLog: this.rebuildLog.map((e) => ({ ...e })),
      trackEvents: this.trackEvents.map((e) => ({ ...e })),
      timings: { ...this.timings },
    };
  }

  /** Frames arrive and the track isn't muted (a muted iOS track still feeds silence). */
  live(withinMs = 600): boolean {
    return this.onFrame !== null && this.lastBlockAt > 0 && performance.now() - this.lastBlockAt < withinMs && !this.trackMuted();
  }

  level(): number {
    if (performance.now() - this.lastBlockAt > 400) return 0; // nothing arriving: show nothing
    return Math.min(1, Math.max(0, (20 * Math.log10(this.rms + 1e-6) + 50) / 40));
  }
}

/** A mute/unmute event of the mic's track: which track (counted per page), the wall-clock time,
 * and how long after the track was acquired. */
export interface TrackEvent {
  track: number;
  ev: "mute" | "unmute" | "muted_at_start";
  at: number;
  sinceTrackMs: number;
}
const TRACK_EVENTS_KEPT = 12;
/** Two prepare()s this close together are the same tap (the player's, then the mic's). */
const SAME_TAP_MS = 1000;
/** A stopped stream is kept, and later reused, only if a frame came this recently. */
const KEEP_IF_FRAME_WITHIN_MS = 1000;
const REBUILDS_KEPT = 8;

function stopTracks(stream: MediaStream): void {
  stream.getTracks().forEach((t) => t.stop());
}

function settingsOf(track: MediaStreamTrack): MediaTrackSettings {
  try {
    return track.getSettings?.() ?? {};
  } catch {
    return {};
  }
}

function rateOf(track: MediaStreamTrack | undefined): number | null {
  return track ? (settingsOf(track).sampleRate ?? null) : null;
}

/** iPhone or iPad (iPadOS says "Macintosh" but has touch). */
export function isAppleTouch(nav: { userAgent?: string; maxTouchPoints?: number } | undefined = typeof navigator !== "undefined" ? navigator : undefined): boolean {
  if (!nav?.userAgent) return false;
  return /iPhone|iPad|iPod/.test(nav.userAgent) || (/Macintosh/.test(nav.userAgent) && (nav.maxTouchPoints ?? 0) > 1);
}

/**
 * The page runs as a home-screen web app (iOS "Add to Home Screen", display-mode standalone).
 * There iOS asks for the mic more often and suspends the page harder in the background.
 */
export function standaloneMode(win: { navigator?: unknown; matchMedia?: (q: string) => { matches: boolean } } | undefined = typeof window !== "undefined" ? window : undefined): boolean {
  if (!win) return false;
  if ((win.navigator as { standalone?: boolean } | undefined)?.standalone === true) return true;
  try {
    return !!win.matchMedia?.("(display-mode: standalone)").matches;
  } catch {
    return false;
  }
}

/** Streaming windowed-sinc resampler: mic rate (usually 48 kHz) to 16 kHz PCM16. ~1 ms latency. */
export class Downsampler {
  private readonly step: number;
  private readonly fc: number;
  private readonly half: number;
  private buf: Float32Array;
  private t: number;

  constructor(private readonly inRate: number, private readonly outRate = MIC_SAMPLE_RATE) {
    this.step = inRate / outRate;
    this.fc = Math.min(1, outRate / inRate) * 0.9;
    this.half = Math.ceil(8 / this.fc);
    this.buf = new Float32Array(this.half); // zero history
    this.t = this.half;
  }

  push(input: Float32Array): Int16Array {
    if (this.inRate === this.outRate) return toInt16(input);
    const merged = new Float32Array(this.buf.length + input.length);
    merged.set(this.buf);
    merged.set(input, this.buf.length);
    const out: number[] = [];
    const { half, fc } = this;
    while (this.t + half < merged.length) {
      const base = Math.floor(this.t);
      let acc = 0;
      for (let i = base - half + 1; i <= base + half; i++) {
        const d = this.t - i;
        const x = Math.PI * fc * d;
        const sinc = d === 0 ? 1 : Math.sin(x) / x;
        const w = 0.42 + 0.5 * Math.cos((Math.PI * d) / half) + 0.08 * Math.cos((2 * Math.PI * d) / half);
        acc += merged[i] * fc * sinc * w;
      }
      out.push(acc);
      this.t += this.step;
    }
    const keep = Math.max(0, Math.floor(this.t) - half);
    this.buf = merged.slice(keep);
    this.t -= keep;
    return toInt16(out);
  }
}

function toInt16(x: ArrayLike<number>): Int16Array {
  const out = new Int16Array(x.length);
  for (let i = 0; i < x.length; i++) out[i] = Math.round(Math.max(-1, Math.min(1, x[i])) * 32767);
  return out;
}
